import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import { getLocalCajaManagementByLocalId } from '../../../../../../../../../crud/management_api/locales_caja_management';
import './CashPaymentModal.scss';

interface CashPaymentModalProps {
  show: boolean;
  onHide: () => void;
  totalDue: number;
  onConfirm: (data: { receivedAmount: number; change: number; workstationId: number }) => void;
}

const CashPaymentModal: React.FC<CashPaymentModalProps> = ({
  show,
  onHide,
  totalDue,
  onConfirm
}) => {
  const [receivedAmount, setReceivedAmount] = useState<number | undefined>(undefined);
  const [shouldRound, setShouldRound] = useState<boolean>(false);
  const [roundingMethod, setRoundingMethod] = useState<'up' | 'down'>('up');
  const [selectedWorkstation, setSelectedWorkstation] = useState<any>(null);
  const [workstations, setWorkstations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Calculate change
  const rawChange = (receivedAmount ?? 0) - totalDue;
  
  let change: number;
  if (!shouldRound) {
    change = rawChange;
  } else {
    if (roundingMethod === 'up') {
      change = Math.ceil(rawChange / 10) * 10;
    } else {
      change = Math.floor(rawChange / 10) * 10;
    }
  }

  // Check if workstation has sufficient funds
  const insufficientChange = Boolean(
    selectedWorkstation &&
    change > 0 &&
    change > (selectedWorkstation.balance ?? selectedWorkstation.current_amount ?? 0)
  );

  useEffect(() => {
    if (show) {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = user.local_id ? Number(user.local_id) : 0;
      
      getLocalCajaManagementByLocalId(localId)
        .then((response: any) => {
          const allCajas = response.data || [];
          const openCajas = allCajas.filter((c: any) => c.is_open);
          setWorkstations(openCajas);
          
          // Auto-select if only one workstation
          if (openCajas.length === 1) {
            setSelectedWorkstation(openCajas[0]);
          }
        })
        .catch((error: any) => {
          console.error('Error fetching workstations:', error);
          toast.error('Error al cargar las cajas');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Reset when modal closes
      setReceivedAmount(undefined);
      setShouldRound(false);
      setRoundingMethod('up');
      setSelectedWorkstation(null);
      setWorkstations([]);
    }
  }, [show]);

  const handleConfirm = () => {
    if (!selectedWorkstation || !selectedWorkstation.workstation_id) {
      toast.error('Debe seleccionar una caja');
      return;
    }
    
    if (receivedAmount === undefined || receivedAmount < totalDue) {
      toast.error('El monto recibido debe ser mayor o igual al total');
      return;
    }
    
    if (insufficientChange) {
      toast.error('No hay suficiente dinero en la caja para dar el cambio');
      return;
    }

    onConfirm({
      receivedAmount,
      change,
      workstationId: selectedWorkstation.workstation_id
    });
  };

  const handleQuickAmount = (amount: number) => {
    setReceivedAmount(amount);
  };

  // Generate quick amount buttons (rounded amounts above total)
  const quickAmounts = [
    Math.ceil(totalDue / 1000) * 1000,
    Math.ceil(totalDue / 5000) * 5000,
    Math.ceil(totalDue / 10000) * 10000,
  ].filter((amount, index, arr) => amount > totalDue && arr.indexOf(amount) === index);

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      className="cash-payment-modal" 
      centered
      backdrop="static"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Recibir Efectivo</Modal.Title>
        </div>
        <Button variant="none" onClick={onHide} className="modal-close-button">
          <FaTimes />
        </Button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="loading-container">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className="cash-payment-form">
            {/* Total Amount */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-label">Total a Pagar</span>
                <span className="section-value total-value">{formatCurrency(totalDue)}</span>
              </div>
            </div>

            {/* Received Amount Input */}
            <div className="form-section">
              <div className="field-label">Monto Recibido</div>
              <div className="amount-input-container">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  className="amount-input"
                  value={receivedAmount ?? ''}
                  onChange={(e) => setReceivedAmount(e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="0"
                  min={0}
                  step={100}
                />
              </div>
              
              {/* Quick amount buttons */}
              {quickAmounts.length > 0 && (
                <div className="quick-amounts">
                  {quickAmounts.slice(0, 3).map((amount) => (
                    <button
                      key={amount}
                      className="quick-amount-btn"
                      onClick={() => handleQuickAmount(amount)}
                    >
                      {formatCurrency(amount)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Change Amount */}
            <div className="form-section">
              <div className="section-header change-section">
                <span className="section-label">Vuelto</span>
                <span className={`section-value ${change >= 0 ? 'change-positive' : 'change-negative'}`}>
                  {formatCurrency(change)}
                </span>
              </div>
            </div>

            {/* Workstation Selection */}
            {workstations.length > 0 && (
              <div className="form-section">
                <div className="field-label">
                  {workstations.length === 1 ? 'Caja Seleccionada' : 'Seleccionar Caja'}
                </div>
                <div className="caja-options">
                  {workstations.map(workstation => (
                    <React.Fragment key={workstation.workstation_id}>
                      <div
                        className={`caja-option ${selectedWorkstation?.workstation_id === workstation.workstation_id ? 'selected' : ''} ${workstations.length === 1 ? 'single-option' : ''}`}
                        onClick={() => setSelectedWorkstation(workstation)}
                      >
                        <div className="caja-name">{workstation.workstation_name}</div>
                        <div className="caja-amount">{formatCurrency(workstation.balance ?? workstation.current_amount ?? 0)}</div>
                      </div>
                      {/* Show error directly under the selected caja when not enough funds */}
                      {selectedWorkstation?.workstation_id === workstation.workstation_id && insufficientChange && (
                        <div className="caja-option-error" key={`err-${workstation.workstation_id}`}>
                          No hay suficiente dinero en esta caja para dar cambio ({formatCurrency(workstation.balance ?? workstation.current_amount ?? 0)} disponible).
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {!loading && workstations.length === 0 && (
              <div className="warning-message info-message">
                <FaExclamationTriangle />
                <span>No hay cajas abiertas disponibles</span>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={onHide}>
            Cancelar
          </Button>
          <Button
            variant="none"
            className="form-button-create"
            onClick={handleConfirm}
            disabled={
              loading ||
              !selectedWorkstation ||
              receivedAmount === undefined ||
              receivedAmount < totalDue ||
              insufficientChange
            }
          >
            Confirmar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CashPaymentModal;
